import Step from 'src/components/Step';
import * as Yup from 'yup';
import { stringValidation, emailValidation, phoneValidation } from 'src/services/validation';
import { useLoaded } from 'src/store/load-context';
import { useAPI } from 'src/services/api';
import ToastContext from 'src/store/toast-context';
import { useState, useEffect, useContext } from 'react';
import handleNull from 'src/util/handleNull';
import ReviewContext from 'src/store/review-page-context';
import ReviewPage from 'src/pages/review';
import ExemptionNotificationModal from 'src/components/ExemptionNotificationModal';
import ItemizeFilingModal from 'src/components/modal/ItemizeFilingModal';
import ExemptionModal from 'src/components/modal/ExemptionModal';
import NeedToItemize from 'src/components/modal/NeedToItemize';
import AuthContext from 'src/store/auth-context';
import formatPhoneNumber from 'src/util/formatPhoneNumber';

const defaultToEmptyString = (val) => (val ? val : '');

const schema = Yup.object().shape({
  submitterName: stringValidation(),
  submitterEmail: emailValidation(),
  submitterPhone: phoneValidation(),
  signed: Yup.boolean().oneOf([true], 'Required').required('Required'),
});

const defaultFormValues = {
  submitterName: '',
  submitterEmail: '',
  submitterPhone: '',
  signed: '',
  remarks: '',
};

const ReviewStep = (props) => {
  const api = useAPI();
  const { setLoaded } = useLoaded();
  const { account, updateAccount } = useContext(AuthContext);
  const { error, handleError } = useContext(ToastContext);
  const { reviewInfo } = useContext(ReviewContext);
  const { review, setReview } = reviewInfo;
  const [initialValues, setInitialValues] = useState(defaultFormValues);
  const forceItemize = review?.total?.count === 0 && account.bypassItemization !== 'Y';

  const fetchInitialFormValues = async () => {
    try {
      setLoaded(false);
      const { data } = handleNull(await api.get('/review'));

      //This is the data needed for the submitters information
      setInitialValues({
        submitterName: defaultToEmptyString(data.updatedInfo.submitterName),
        submitterEmail: defaultToEmptyString(data.updatedInfo.submitterEmail),
        submitterPhone: defaultToEmptyString(formatPhoneNumber(data.updatedInfo.submitterPhone)),
        signed: '',
        remarks: defaultToEmptyString(data.updatedInfo.remarks),
      });

      //This is the data that is needed for the review section
      setReview(data);

      setLoaded(true);
    } catch (err) {
      console.error(err);
      handleError(err);
    }
  };

  const submitFormData = async (data) => {
    try {
      if (forceItemize) {
        error('You must enter your assets on Step 3');
        return;
      }
      setLoaded(false);
      await api.patch('/filing/submit', {
        ...data,
        signed: data.signed ? 'Y' : 'N',
      });

      updateAccount({ signed: data.signed ? 'Y' : 'N' });

      setLoaded(true);
    } catch (err) {
      setLoaded(true);
      if (err.response.status === 400) {
        error(
          'Please ensure all required information is completed in Steps 1 and 2 before submitting.'
        );
      } else {
        handleError(err);
      }
    }
  };

  useEffect(() => {
    fetchInitialFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Step
      component={<ReviewPage />}
      initialValues={initialValues}
      onComplete={submitFormData}
      path={['/review']}
      schema={schema}
      modals={[
        <ExemptionModal key="exemptionModal" />,
        <ItemizeFilingModal key="itemizeModal" />,
        <ExemptionNotificationModal key="exemptNotiModal" />,
        <NeedToItemize key="needItemizationModal" />,
      ]}
      {...props}
    />
  );
};

export default ReviewStep;
